import { Wrap, WrapItem } from "@chakra-ui/react";
import { ChevronRight as ChevronRightIcon, ExternalLink as ExternalLinkIcon, FileText as FileTextIcon } from "lucide-react";
import { ChatAvatar } from "@root/components/chat/ChatAvatar";
import { CHAT_COLORS, CHAT_FONT, chatMarkdownSx } from "@root/components/chat/chatStyles";
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  Badge,
  SimpleGrid,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  Image,
  Spinner,
} from "@chakra-ui/react";
import { CustomText } from "@root/components/common/components";
import { useTranslation } from "react-i18next";
import { NewTabIcon } from "@root/assets/icons/NewTabIcon";
import MarkdownContent from "@root/components/markdown/markdown";
import { CitationModal } from "@root/components/LegalComplianceChatbot/CitationModal";
import { SourceContextMenu } from "@root/components/LegalComplianceChatbot/SourceContextMenu";
import { ChevronRight } from "@root/assets/icons/chevron-right";
import LinkIconNew from "@root/assets/icons/LinkIcon.svg";
import OpenNewIcon from "@root/assets/icons/OpenNewIcon.svg";
// import UploadIcon from "@root/assets/icons/UploadIcon.svg";
import DownloadIcon from "@root/assets/icons/DownloadIcon.svg";
// import ReloadIcon from "@root/assets/icons/ReloadIcon.svg";
import CopyIconNew from "@root/assets/icons/CopyIconNew.svg";
import { AI, COPY_DELAY, USER } from "@root/constants";
import { formatDateTime } from "@root/utils/date-helper";
import { DATE_FORMATS, PDF_LOADING_ACTION } from "@root/constants";
import { useState, useEffect, useCallback } from "react";
import { Tooltip, useClipboard } from "@chakra-ui/react";
import { TickIcon } from "@root/assets/admin/icons/Tick";
import { ChatService } from "@root/services";
import useTypewriterEffect from "@root/components/dashboard/chat/roles/system/useTypewriterEffect";
import ChatFollowUpQuestions from "@root/pages/dashboard/components/ChatFollowUpQuestions";
import { fetchPdfUrl } from "@root/hooks/useRiskAssessments";

interface MultiAIChatMessageProps {
  message: {
    id: number;
    type: typeof USER | typeof AI;
    content: string;
    timestamp: string;
    modelType?: number;
    sources?: any[];
    followUp?: any[];
    // Restored history / error notices render without the typewriter
    skipTypewriter?: boolean;
    isError?: boolean;
  };
  aiModelOptions: { id: number; label: string }[];
  showActions?: boolean;
  currentStatus?: any;
  completedMessageIds?: Set<number>;
  onSubmitHandler?: (question: string) => void;
  isPaused?: boolean;
  isStreaming?: boolean;
  showFollowUps?: boolean;
}

export const MultiAIChatMessage = ({
  message,
  aiModelOptions,
  showActions,
  completedMessageIds,
  onSubmitHandler,
  isPaused = false,
  isStreaming = false,
  showFollowUps = true,
}: MultiAIChatMessageProps) => {
  const { t } = useTranslation("common");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isCitedTextModalOpen,
    onOpen: onCitedTextModalOpen,
    onClose: onCitedTextModalClose,
  } = useDisclosure();
  const [loadingSources, setLoadingSources] = useState<{
    [key: number]: boolean;
  }>({});
  const [copied, setCopied] = useState<{ [key: number]: boolean }>({});
  const [menuPosition, setMenuPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [selectedSourceItem, setSelectedSourceItem] = useState<any>(null);
  const [loadingAction, setLoadingAction] = useState<PDF_LOADING_ACTION | null>(
    null
  );
  const [citedContext, setCitedContext] = useState<string>("");

  const baseAuthUrl = process.env.REACT_APP_BASE_URL || "";
  const chatService = new ChatService();
  const [isDownloading, setIsDownloading] = useState(false);

  // Use typewriter effect for AI messages
  const { displayText, isComplete } = useTypewriterEffect(
    message?.content || "",
    {
      delay: 4, // Speed of typing
      isStreaming: true, // Enable streaming mode for better handling of dynamic content
      // Restored history and error notices appear instantly
      disableAnimation: Boolean(message?.skipTypewriter),
    }
  );

  // Clipboard support for copying message content
  const plainTextContent = (message?.content || "").replace(/<[^>]+>/g, "");
  const { onCopy } = useClipboard(plainTextContent);

  const handleCopy = () => {
    try {
      onCopy();
      setCopied({ 0: true });
      setTimeout(() => setCopied({ 0: false }), COPY_DELAY);
    } catch (e) {
      // no-op
    }
  };

  const handleSourceClick = async (source: any, index: number) => {
    try {
      let name = source?.file_name?.split("_");
      let file_path = source?.folder_name + "/" + name[name?.length - 1];
      // Set loading state for this specific source
      setLoadingSources((prev) => ({ ...prev, [index]: true }));

      // Make API call using service
      const response = await chatService.getChatFilePath(baseAuthUrl, {
        filePath: file_path,
      });

      // Open the file in a new tab
      if (response?.data?.data?.fileUrl) {
        window.open(response.data.data.fileUrl, "_blank");
      }
    } catch (error) {
      console.error("Error fetching file path:", error);
      // Fallback to original URL if API fails
      if (source?.signedUrl || source?.url) {
        window.open(source.signedUrl || source.url, "_blank");
      }
    } finally {
      // Remove loading state
      setLoadingSources((prev) => ({ ...prev, [index]: false }));
    }
  };

  const handleLinkClick = (text: string, url: string, event?: MouseEvent) => {
    console.log("text", text, message?.sources);
    // Prevent default link behavior
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (text) {
      // Find source item using utility function
      // const sourceItem = findSourceByText(text, message?.sources || []);
      const sourceItem = message?.sources?.find((item: any) =>
        item?.file_name?.includes(text)
      );
      console.log("sourceItem", sourceItem);
      // if (!sourceItem) {
      //   console.warn("Source item not found for text:", text);
      //   // Fallback: try to open the URL directly if available
      //   // if (url) {
      //   //   window.open(url, "_blank", "noopener,noreferrer");
      //   // }
      //   // return;
      // }

      // Store the source item and show menu at click location
      setSelectedSourceItem(sourceItem);
      if (event) {
        setMenuPosition({ x: event.clientX, y: event.clientY });
      }
    }
  };

  const handleOpenInNewTab = async () => {
    if (!selectedSourceItem || loadingAction) return;

    setLoadingAction(PDF_LOADING_ACTION.NEW_TAB);
    try {
      const fileUrl = await fetchPdfUrl(
        selectedSourceItem,
        baseAuthUrl,
        chatService
      );
      if (fileUrl) {
        window.open(fileUrl, "_blank");
      }
    } catch (error) {
      console.error("Error opening PDF:", error);
    } finally {
      setLoadingAction(null);
      handleCloseContextMenu();
    }
  };

  const handleViewCitedText = () => {
    if (selectedSourceItem?.cited_context) {
      setCitedContext(selectedSourceItem.cited_context);
    } else {
      setCitedContext("");
    }
    handleCloseContextMenu();
    onCitedTextModalOpen();
  };

  const handleCloseContextMenu = useCallback(() => {
    setMenuPosition(null);
    setSelectedSourceItem(null);
  }, []);

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = () => {
      if (menuPosition) {
        handleCloseContextMenu();
      }
    };

    if (menuPosition) {
      document.addEventListener("click", handleClickOutside);
      return () => {
        document.removeEventListener("click", handleClickOutside);
      };
    }
  }, [menuPosition, handleCloseContextMenu]);

  const getGmtOffsetMinutes = () => {
    try {
      return -new Date().getTimezoneOffset();
    } catch {
      return 0;
    }
  };

  const handleExportChat = async () => {
    try {
      if (!message?.id) return;
      setIsDownloading(true);
      const payload = {
        chatIds: [message.id],
        gmtOffset: getGmtOffsetMinutes(),
      };
      // Request a PDF blob from export-chat and download as .pdf
      const res: any = await chatService.exportChats(baseAuthUrl, payload);
      const blob = res?.data;
      const headers = res?.headers || {};
      const contentType = headers["content-type"] || "application/pdf";
      const disposition: string | undefined = headers["content-disposition"];
      const match = disposition?.match(/filename\*?=(?:UTF-8''|")?([^";]+)"?/);
      const filename = match?.[1] || `chat.pdf`;

      if (blob instanceof Blob) {
        const url = window.URL.createObjectURL(
          new Blob([blob], { type: contentType })
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (e) {
    } finally {
      setIsDownloading(false);
    }
  };

  const MAX_SOURCE_CHIPS = 4;
  const sourceName = (source: any, index: number) =>
    source?.file_name ||
    source?.korean_filename ||
    source?.filename ||
    `Source ${index + 1}`;

  // Compact source chips; "View all" opens the drawer with every source
  const renderSourcesCard = () => {
    const sources: any[] = message?.sources || [];
    if (!sources.length) return null;
    return (
      <Box w="100%">
        <Text fontSize="12px" fontWeight="600" color={CHAT_COLORS.muted} mb="8px">
          {t("multiAI.sources")} · {sources.length}
        </Text>
        <Wrap spacing="8px">
          {sources.slice(0, MAX_SOURCE_CHIPS).map((source, index) => (
            <WrapItem key={index}>
              <HStack
                as="button"
                type="button"
                spacing="6px"
                px="10px"
                py="6px"
                maxW="280px"
                bg="white"
                border={`1px solid ${CHAT_COLORS.border}`}
                borderRadius="8px"
                transition="all 0.15s"
                _hover={{ borderColor: CHAT_COLORS.brand, bg: CHAT_COLORS.hoverBg }}
                onClick={() => handleSourceClick(source, index)}
                aria-label={sourceName(source, index)}
              >
                <FileTextIcon size={14} color={CHAT_COLORS.brand} style={{ flexShrink: 0 }} />
                <Text fontSize="13px" color={CHAT_COLORS.body} noOfLines={1}>
                  {sourceName(source, index)}
                </Text>
                {loadingSources[index] ? (
                  <Spinner size="xs" color={CHAT_COLORS.brand} />
                ) : (
                  <ExternalLinkIcon size={12} color={CHAT_COLORS.subtle} style={{ flexShrink: 0 }} />
                )}
              </HStack>
            </WrapItem>
          ))}
          {sources.length > MAX_SOURCE_CHIPS && (
            <WrapItem>
              <HStack
                as="button"
                type="button"
                spacing="2px"
                px="10px"
                py="6px"
                borderRadius="8px"
                color={CHAT_COLORS.brand}
                _hover={{ bg: CHAT_COLORS.hoverBg }}
                onClick={onOpen}
              >
                <Text fontSize="13px" fontWeight="500">
                  {t("multiAI.viewAllSources")}
                </Text>
                <ChevronRightIcon size={14} />
              </HStack>
            </WrapItem>
          )}
        </Wrap>
      </Box>
    );
  };

  const renderFollowUpQuestions = () => {
    if (showFollowUps && message?.followUp && message?.followUp?.length > 0)
      return (
        <ChatFollowUpQuestions
          followUpQuestions={message?.followUp}
          onSubmitHandler={onSubmitHandler}
        />
      );
  };

  const renderMessages = () => {
    return (
      <VStack w="100%" align="stretch" spacing={4}>
        {renderSourcesCard()}
        {/* Follow Up Questions */}
        {renderFollowUpQuestions()}

        {/* All Sources Drawer */}
        <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="md">
          <DrawerOverlay />
          <DrawerContent bg={"white"} fontFamily={CHAT_FONT}>
            <DrawerHeader borderBottomWidth="1px">
              <HStack justify="space-between" align="center">
                <Text fontSize="18px" fontWeight="600" color={"#005EB8"}>
                  {t("multiAI.sources")} ({message?.sources?.length})
                </Text>
                <DrawerCloseButton position="static" />
              </HStack>
            </DrawerHeader>
            <DrawerBody p={4}>
              <VStack align="stretch" spacing={3}>
                {message?.sources?.map((source, index) => (
                  <Box
                    key={index}
                    p="12px"
                    borderRadius="8px"
                    border="1px solid #E5E7EB"
                    bg="white"
                  >
                    <HStack justify="space-between" align="start">
                      <HStack spacing={3} align="start" flex={1}>
                        {/* <LinkIcon boxSize={4} color="#6B7280" mt={1} /> */}
                        <Image src={LinkIconNew} />
                        <VStack align="start" spacing={2} flex={1}>
                          <Text
                            fontSize="15px"
                            fontWeight="600"
                            color="#111827"
                          >
                            {source?.file_name ||
                              source?.korean_filename ||
                              source?.filename ||
                              `Source ${index + 1}`}
                          </Text>
                          {(source?.content || source?.description) && (
                            <Text
                              fontSize="13px"
                              color="#6B7280"
                              lineHeight="1.4"
                            >
                              {source?.content || source?.description}
                            </Text>
                          )}
                        </VStack>
                        <Image
                          src={OpenNewIcon}
                          cursor="pointer"
                          onClick={() => handleSourceClick(source, index)}
                        />
                      </HStack>
                      {(source?.signedUrl || source?.url) && (
                        <Box
                          cursor="pointer"
                          aria-label="Open source"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          width="20px"
                          height="20px"
                        >
                          {loadingSources[index] ? (
                            <Spinner size="sm" color="#005EB8" />
                          ) : (
                            <NewTabIcon />
                          )}
                        </Box>
                      )}
                    </HStack>
                  </Box>
                ))}
              </VStack>
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      </VStack>
    );
  };

  if (message?.type === USER) {
    return (
      <Flex justify="flex-end" mb={6}>
        <VStack align="flex-end" spacing="4px" maxW="78%">
          <Box
            bg={CHAT_COLORS.userBubble}
            color={CHAT_COLORS.text}
            px="16px"
            py="10px"
            borderRadius="18px"
            borderBottomRightRadius="6px"
          >
            <Text fontSize="15px" lineHeight="1.6" whiteSpace="pre-wrap" wordBreak="break-word">
              {message?.content}
            </Text>
          </Box>
          <Text fontSize="11px" color={CHAT_COLORS.subtle} pr="4px">
            {formatDateTime(message?.timestamp, DATE_FORMATS.DATE_AND_TIME)}
          </Text>
        </VStack>
      </Flex>
    );
  }

  const downloadIcon = () => {
    if (isDownloading) return <Spinner size="sm" color="#005EB8" />;
    return <Image src={DownloadIcon} cursor={"pointer"} />;
  };

  return (
    <Flex justifyContent="flex-start" alignItems="flex-start" mb={6} gap="10px">
      <ChatAvatar />
      <Box flex="1" minW={0}>
        <VStack align="start" spacing={3}>
          <HStack spacing={2} h="26px">
            <Text fontSize="13px" fontWeight="600" color={CHAT_COLORS.text}>
              {t("doosanAI.title")}
            </Text>
            <Text fontSize="12px" color={CHAT_COLORS.subtle}>
              {formatDateTime(message?.timestamp, DATE_FORMATS.DATE_AND_TIME)}
            </Text>
          </HStack>
          {message?.isError ? (
            <Box px="12px" py="10px" bg="#FEF2F2" border="1px solid #FECACA" borderRadius="10px">
              <Text fontSize="14px" color="#B91C1C">
                {message?.content}
              </Text>
            </Box>
          ) : (
            <Box w="100%" sx={chatMarkdownSx}>
            <MarkdownContent
              content={displayText}
              p={0}
              onLinkClick={handleLinkClick}
              disableLinks={
                !completedMessageIds?.has(message?.id) &&
                !message?.sources?.length
              }
            />
            </Box>
          )}
          {/* Action icons only when response is finalized and typewriter is complete */}
          {/* Show actions if: message has content, typewriter is complete, and this message is not currently streaming */}
          {showActions && message?.content && isComplete && !isStreaming && !message?.isError ? (
            <HStack
              spacing="2px"
              ml="-6px"
              color={CHAT_COLORS.muted}
              sx={{ "& > div": { p: "6px", borderRadius: "6px" }, "& > div:hover": { bg: "#F1F5F9" } }}
            >
              {completedMessageIds?.has(message?.id) ? (
                <Tooltip hasArrow label={t("translate.downloadToClipboard")}>
                  <Box
                    cursor="pointer"
                    onClick={isDownloading ? undefined : handleExportChat}
                  >
                    {/* Download icon */}
                    {downloadIcon()}
                  </Box>
                </Tooltip>
              ) : null}
              {/* <Image src={ReloadIcon} /> */}
              <Tooltip
                hasArrow
                label={
                  copied[0]
                    ? t("translate.copiedToClipboard")
                    : t("translate.copyToClipboard")
                }
              >
                <Box cursor="pointer" onClick={handleCopy}>
                  {copied[0] ? (
                    <TickIcon color="#10B981" />
                  ) : (
                    <Image src={CopyIconNew} cursor={"pointer"} />
                  )}
                </Box>
              </Tooltip>
            </HStack>
          ) : null}
          {renderMessages()}
        </VStack>
      </Box>
      {/* Context Menu */}
      <SourceContextMenu
        menuPosition={menuPosition}
        selectedSourceItem={selectedSourceItem}
        loadingAction={loadingAction}
        onOpenInNewTab={handleOpenInNewTab}
        onViewCitedText={handleViewCitedText}
        onClose={handleCloseContextMenu}
      />
      {/* Cited Text Reference Modal */}
      <CitationModal
        isOpen={isCitedTextModalOpen}
        onClose={() => {
          onCitedTextModalClose();
          setCitedContext("");
        }}
        citedContext={citedContext}
      />
    </Flex>
  );
};
